// ADR 23.9.26u §12 — the one install event.
//
// `web_`-prefixed because it is web-only (6.9.26p): a native client is
// installed from a store and has no `appinstalled`. The `installStrip.ts`
// shape — named constant, sink seam for tests — without its delivery latch:
// `appinstalled` fires long after `initTelemetry` settles, so there is nothing
// to park and retry.
//
// **iOS never fires `appinstalled`**, so this undercounts iOS installs by
// construction; an iOS install is visible only as sessions carrying the
// `display_mode: standalone` session property (`telemetry/index.ts`).

import { capture } from './capture'

export const PWA_INSTALLED_EVENT = 'web_pwa_installed'

/**
 * `prompt`: our "Install app" was accepted in this page. `steps`: our steps
 * panel was shown in this page (ADR 15.10.26d §4). `browser`: the omnibox or
 * menu, unprompted.
 */
export type InstallVia = 'prompt' | 'steps' | 'browser'

export type PwaSink = (name: string, properties: Record<string, unknown>) => void

let sink: PwaSink = capture

/** Test seam: watch the event without attaching an SDK. */
export function setPwaSink(replacement: PwaSink): void {
  sink = replacement
}

/** Put 6.9.26c's `capture()` back — the production path. */
export function resetPwaSink(): void {
  sink = capture
}

/**
 * ADR 15.10.26d §4 — only a `steps` install carries `platform` (the steps it
 * followed); the `prompt` and `browser` wire is byte-identical to before.
 * `platform` is `pwa/installAffordance.ts`'s, passed as a string so this
 * module imports nothing from `pwa/`.
 */
export function emitPwaInstalled(via: InstallVia, platform?: string): void {
  sink(PWA_INSTALLED_EVENT, via === 'steps' && platform !== undefined ? { via, platform } : { via })
}

/**
 * ADR 6.10.26a §4 — the browser's install dialogue was answered. Fires from a
 * tap, long after init, so it needs no latch. `web_pwa_installed` sees accepts
 * only; this one sees the dismissals too.
 */
export const INSTALL_PROMPT_RESULT_EVENT = 'web_install_prompt_result'

/**
 * Which CTA opened the prompt. Restated rather than imported from `pwa/`, as
 * `installStrip.ts` restates `StripAffordance`.
 */
export type InstallSurface = 'strip' | 'header' | 'door' | 'account'

export function emitInstallPromptResult(outcome: 'accepted' | 'dismissed', surface: InstallSurface): void {
  sink(INSTALL_PROMPT_RESULT_EVENT, { outcome, surface })
}
